import React from "react";
import { applyGeotabCalibration } from "../../api/geotabApi.js";
import ConfirmationModal from "../ConfirmationModal.jsx";
import { useToast } from "../ResultsPrimitives.jsx";
import { createToastProgress, createToastProgressReporter } from "../../utils/toastProgress.js";
import { describeDispatchRefresh, refreshDispatchDay, resolveActiveDispatchDate } from "../../utils/dispatchDayRefresh.js";
import GeotabBadge from "./GeotabBadge.jsx";
import { HoverRows } from "./GeotabHover.jsx";
import { dayEndTrend, fmtDelta, fmtDuration } from "./geotabFormat.js";
import { LINK_CLASS as LINK } from "./geotabLinks.js";

const CELL = "border-t border-slate-100 px-2 py-1 text-[12.5px] tabular-nums";
const HEAD = "px-2 py-1 text-left text-[10.5px] font-bold uppercase tracking-wide text-slate-500";
const SOURCES = { setting: "Paramètres système", site: "Clients et sites", employee: "Chauffeurs" };
// Onglet où la fiche se modifie : la vitesse et les débits d'un dompeur (une seule pièce) dans Camions, ceux d'une remorque dans Remorques.
const sourceOf = (c) => (c.kind !== "truck" ? SOURCES[c.kind] : c.field === "avg_speed_kmh" || c.per_truck ? "Camions" : "Remorques");
const NOTES = {
  loading: "Temps fixe et débit vont ensemble ; chaque supplément se décoche à part. Chaque dompeur a son débit.",
  delivery: "Temps fixe et débits par type de camion vont ensemble. Chaque dompeur a son débit.",
  route: "Temps fixe par trajet et vitesse vont ensemble ; même vitesse pour tous les camions.",
  aspiration: "Mission = aspiration + livraison : temps aspirateur de la remorque (la base) + tonnes au débit d'aspiration, qui vont ensemble.",
  lunch: "Durée : tous les chauffeurs. Heure de début (défaut dès 10 dîners, chauffeur dès 5) : proposée à 30 min d'écart et plus.",
};
const MEASURES = { loading: "chargements", delivery: "livraisons", route: "trajets", aspiration: "missions", lunch: "dîners" };
const TREND_CLASS = { worse: "text-[#a86a24]", better: "text-accent-strong", same: "" };

// Fin de journée avec les suggestions du groupe : juste arrêt par arrêt ne veut pas dire juste sur la journée.
function DayEnd({ before, after }) {
  if (!before?.count || !after?.count) return null;
  const shift = Math.round(after.median_delta - before.median_delta);
  return (
    <HoverRows title="Fin de journée" rows={[["Aujourd'hui", fmtDelta(before.median_delta)], ["Avec ces réglages", fmtDelta(after.median_delta)],
      shift !== 0 && ["Effet", `fin prévue ${Math.abs(shift)} min plus ${shift > 0 ? "tôt" : "tard"}`],
      ["Mesure", "retour réel − prévu, médiane"], ["Journées", `${after.count} camions-jours propres`]]}>
      <span className="cursor-help text-[11.5px] text-slate-600">Fin de journée <b className="tabular-nums">{`${fmtDelta(before.median_delta)} → `}
        <span className={TREND_CLASS[dayEndTrend(before.median_delta, after.median_delta)]}>{fmtDelta(after.median_delta)}</span></b></span>
    </HoverRows>
  );
}

// « h » : heure du jour en minutes depuis minuit (dîner), affichée « 11 h 55 » et saisie « 11:55 ».
const fmtClock = (minutes) => `${Math.floor(Math.round(minutes) / 60)} h ${String(Math.round(minutes) % 60).padStart(2, "0")}`;
const fmtValue = (value, unit) => (value == null ? "—" : unit === "h" ? fmtClock(value) : `${Math.round(value)} ${unit}`);
const fmtInput = (value, unit) => (unit === "h" ? fmtClock(value).replace(" h ", ":") : Math.round(value));
const parseClock = (text) => {
  const match = String(text).trim().match(/^(\d{1,2})\s*(?:h|:)\s*(\d{2})?$/i);
  return match && Number(match[1]) < 24 && Number(match[2] || 0) < 60 ? Number(match[1]) * 60 + Number(match[2] || 0) : null;
};
const distinct = (values, unit) => [...new Set(values.map((v) => (v == null ? "—" : unit === "h" ? fmtClock(v) : Math.round(v))))];
// Valeur actuelle d'une ligne. Chauffeur sans heure dans sa fiche : il dîne à l'heure par défaut.
const currentOf = (row) => {
  const [first] = row.items;
  const values = first.current == null && first.default != null ? [`${fmtClock(first.default)} (défaut)`] : distinct(row.items.map((c) => c.current), first.unit);
  return first.unit === "h" ? values.join(" ou ") : `${values.join(" ou ")} ${first.unit}`;
};

// Débit commun (et base d'aspiration commune) : les camions qui reçoivent la même valeur tiennent sur une ligne, le détail en bulle.
const SHARED_FIELDS = new Set(["loading_rate_tons_per_hour", "unloading_rate_tons_per_hour", "avg_speed_kmh", "aspirator_service_minutes"]);
// Temps fixe et débit : s'appliquent ensemble, sinon le même écart est corrigé deux fois ou pas du tout.
const isBase = (c) => c.kind === "setting" || SHARED_FIELDS.has(c.field);

function rowsOf(changes) {
  const rows = [];
  const byValue = new Map();
  changes.forEach((c) => {
    // Débit propre à un dompeur (per_truck) : sa ligne, sa valeur.
    if (!SHARED_FIELDS.has(c.field) || c.per_truck) {
      const label = c.kind === "site" ? `Supplément · ${c.label}` : c.kind === "truck" || c.kind === "employee" ? `${c.field_label} · ${c.label}` : c.field_label;
      rows.push({ key: `${c.kind}-${c.id}-${c.field}`, label, items: [c] });
      return;
    }
    const key = `${c.field}-${c.suggested}`;
    if (!byValue.has(key)) {
      const row = { key, items: [] };
      byValue.set(key, row);
      rows.push(row);
    }
    byValue.get(key).items.push(c);
  });
  return rows.map((row) => {
    const [first] = row.items;
    if (!SHARED_FIELDS.has(first.field)) return row;
    const names = row.items.map((c) => c.label);
    // Débits et temps aspirateur : champs de la remorque (onglet Remorques) ; la vitesse est celle du camion.
    const noun = first.field === "avg_speed_kmh" ? "Camions" : "Remorques";
    return { ...row, label: `${first.field_label} · ${names.length > 3 ? `${names.length} ${noun.toLowerCase()}` : names.join(", ")}`, names, noun };
  });
}

// Valeur essayée : celle tapée, sinon la suggestion. Un débit ou une vitesse doit rester au-dessus de 0.
function valueOf(row, typed) {
  const [first] = row.items;
  if (typed == null) return first.suggested;
  if (first.unit === "h") return parseClock(typed);
  const value = Number(String(typed).replace(",", "."));
  // Supplément négatif : meunerie plus rapide que le temps fixe par chargement (jusqu'à −120 min, comme le Data Manager).
  const min = first.field === "meunerie_loading_extra_minutes" ? -120 : 0;
  return typed !== "" && Number.isFinite(value) && (value > 0 || (first.unit === "min" && value >= min)) ? value : null;
}

// Lignes cochées des groupes, avec la valeur essayée. Tapée à la place de la suggestion, c'est un choix que le Calibrage retient.
const isChosen = (row, typed) => typed != null && valueOf(row, typed) !== row.items[0].suggested;
function selectedChanges(groups, unchecked, typed) {
  return groups.flatMap((group) => rowsOf(group.changes)
    .filter((row) => !unchecked.has(row.key) && valueOf(row, typed[row.key]) != null)
    .flatMap((row) => row.items.map((c) => ({ ...c, suggested: valueOf(row, typed[row.key]), chosen: isChosen(row, typed[row.key]) }))));
}

// Plage probable : la même mesure refaite sur des tirages au hasard des journées, du 10e au 90e rang. Étroite : la valeur
// ne dépend pas des journées retenues. Classée par le serveur (spread) ; Instable et Trop peu ne sont pas proposées.
const fmtRange = ([low, high], unit) => (low === high ? fmtValue(low, unit)
  : unit === "h" ? `${fmtClock(low)} à ${fmtClock(high)}` : `${Math.round(low)} à ${Math.round(high)} ${unit}`);
const SPREADS = { stable: ["ok", "Stable"], medium: ["mute", "Moyen"] };

function Spread({ spread, range, unit }) {
  if (!SPREADS[spread]) return <GeotabBadge tone="empty" />;
  const [tone, label] = SPREADS[spread];
  return <GeotabBadge tone={tone} title={`${fmtRange(range, unit)} selon les journées retenues`}>{label}</GeotabBadge>;
}

// Pas proposée, la valeur actuelle reste : mesure trop variable ou trop rare (les autres valeurs du groupe sont calculées pour elle),
// ou groupe sans gain sur une autre quinzaine (dit une fois par Validation).
function HeldRow({ row, measures, noun }) {
  const [first] = row.items;
  const why = first.spread === "few" ? `sur ${measures} ${noun} seulement` : `instable (${fmtRange(first.range, first.unit)})`;
  return (
    <p className="mx-1 mt-1 text-[11.5px] text-slate-600">
      <b className="font-semibold text-slate-800">{row.label}</b>
      {first.reason === "no_gain" ? ` : mesuré ${fmtValue(first.suggested, first.unit)}, l'actuel ${currentOf(row)} est gardé.`
        : ` : mesuré ${fmtValue(first.suggested, first.unit)}, mais ${why} : pas proposé, l'actuel ${currentOf(row)} est gardé.`}
    </p>
  );
}

// Réglages d'une quinzaine essayés sur la suivante : un gain d'au moins une demi-minute par visite ou trajet, sinon rien n'est proposé,
// sauf le débit propre à un dompeur (jugé sur ses visites : kept).
function Validation({ check, kept = false }) {
  const fmt = (minutes) => `${minutes.toLocaleString("fr-CA", { maximumFractionDigits: 1 })} min`;
  return (
    <p className="mx-1 mb-1 flex flex-wrap items-center gap-2 text-[11.5px] text-slate-600">
      <HoverRows title="Vérifié sur une autre quinzaine" rows={[["Mesuré sur", `${check.days[0]} journées`], ["Essayé sur", `les ${check.days[1]} suivantes`],
        ["Écart", "médiane, en plus ou en moins"]]}>
        <span className="cursor-help">{`Vérifié sur une autre quinzaine : écart ${fmt(check.before)} → ${fmt(check.after)}${
          check.gain ? "" : kept ? ", pas de gain : seuls les débits propres aux dompeurs, jugés sur leurs visites, sont proposés."
            : ", pas de gain : rien n'est proposé, les valeurs actuelles sont gardées."}`}</span>
      </HoverRows>
      {check.gain && <span className="inline-block w-24"><GeotabBadge tone="ok">Gain confirmé</GeotabBadge></span>}
    </p>
  );
}

// Durée habituelle et horaire prudent côte à côte : rien n'est appliqué, la valeur se choisit plus tard.
function PrudentTable({ prudent }) {
  const { habitual, day_length: day = {} } = prudent;
  const route = (r) => (r?.fixed_min != null ? `${r.fixed_min} min par trajet + ${r.speed_kmh} km/h` : "—");
  const minutes = (value) => (value != null ? `${Math.round(value)} min` : "—");
  // Durée du plan comparée aux réglages d'aujourd'hui, pas à l'autre colonne.
  const share = (d) => (!d?.count ? "—" : `${d.on_time_pct} % · ${d.longer
    ? `plan ${Math.abs(d.longer)} min plus ${d.longer > 0 ? "long" : "court"}` : "même plan"} qu'aujourd'hui`);
  const rows = [
    ["Route", route(habitual.route), route(prudent.route)],
    ["Temps fixe par livraison", minutes(habitual.delivery_fixed), minutes(prudent.delivery_fixed)],
    ["Chargement", "valeur du site", prudent.loading_margin != null ? `valeur du site + ${prudent.loading_margin} min` : "—"],
    ["Camions-jours propres finis à l'heure ou avant", share(day.habitual), share(day.careful)],
  ];
  return (
    <section className="min-w-0 overflow-x-auto rounded-[4px] border border-slate-200 bg-white px-2 py-1.5">
      <div className="px-1 text-[12.5px] font-bold text-slate-800">Durée habituelle et horaire prudent</div>
      <p className="px-1 pb-1 text-[11px] text-slate-500">Habituel : la valeur du milieu, la moitié des cas dure moins, l'autre plus. Prudent : chaque valeur couvre 8 cas sur 10.</p>
      <table className="w-auto min-w-0">
        <thead><tr>{["", "Habituel (médiane)", "Prudent (8 fois sur 10)"].map((label) => <th key={label} className={HEAD}>{label}</th>)}</tr></thead>
        <tbody>{rows.map(([label, usual, careful]) => (
          <tr key={label}><td className={`${CELL} font-semibold text-slate-800`}>{label}</td><td className={CELL}>{usual}</td><td className={CELL}>{careful}</td></tr>
        ))}</tbody>
      </table>
    </section>
  );
}

function ChangeRow({ row, measures, checked, typed, onToggle, onType }) {
  const [first] = row.items;
  const value = valueOf(row, typed);
  const edited = typed != null;
  const label = <span className="font-semibold text-slate-800">{row.label}</span>;
  return (
    <tr className={checked ? undefined : "text-slate-400"}>
      <td className={`${CELL} max-w-[340px] truncate`}>
        <label className="inline-flex cursor-pointer items-center gap-2">
          <input type="checkbox" checked={checked} onChange={onToggle} className="accent-[var(--color-accent)]" />
          {row.names?.length > 3 ? <HoverRows title={first.field_label} rows={[[row.noun, row.names.join(", ")]]}><span className="cursor-help">{label}</span></HoverRows> : label}
        </label>
      </td>
      <td className={`${CELL} text-slate-500`}>{sourceOf(first)}</td>
      <td className={`${CELL} text-slate-500`}>{currentOf(row)}</td>
      <td className={`${CELL} whitespace-nowrap`}>
        <input type="text" inputMode={first.unit === "h" ? "text" : "decimal"} aria-label={`Valeur essayée : ${row.label}`} value={edited ? typed : fmtInput(first.suggested, first.unit)} disabled={!checked}
          onChange={(e) => onType(e.target.value)}
          className={`h-6 w-14 rounded-[4px] border bg-white px-1 py-0 text-right text-[12.5px] tabular-nums focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40 disabled:bg-slate-50 ${
            value == null ? "border-[#c5524a]" : edited ? "border-accent font-semibold" : "border-slate-300"}`} />
        {first.unit !== "h" && <span className="ml-1 text-[11px] text-slate-500">{first.unit}</span>}
        {edited && <span className="ml-1.5 text-[11px] text-slate-500">{`suggéré ${fmtValue(first.suggested, first.unit)}`}</span>}
      </td>
      <td className={`${CELL} text-slate-500`}>{measures || "—"}</td>
      <td className={`${CELL} whitespace-nowrap`}>
        <span className="inline-flex items-center gap-2">
          {first.range && <span className="text-slate-600">{fmtRange(first.range, first.unit)}</span>}
          <span className="inline-block w-20"><Spread spread={first.spread} range={first.range} unit={first.unit} /></span>
        </span>
      </td>
    </tr>
  );
}

function GroupCard({ group, dayEnd, unchecked, typed, onToggle, onType, onApply, onForget }) {
  const rows = rowsOf(group.changes);
  const chosenRows = rowsOf(group.chosen || []);
  const selected = selectedChanges([group], unchecked, typed);
  const base = rows.filter((row) => isBase(row.items[0]));
  const partialBase = group.key !== "lunch" && base.some((row) => unchecked.has(row.key)) && base.some((row) => !unchecked.has(row.key));
  // Une valeur tapée et appliquée seule : retenue, le prochain calcul donne celles qui vont avec.
  const chosenBase = base.some((row) => !unchecked.has(row.key) && isChosen(row, typed[row.key]));
  // Débit commun : mesuré sur toutes les visites du groupe, pas camion par camion.
  const measuresOf = (row) => row.items.reduce((sum, c) => sum + (c.measures || 0), 0) || (SHARED_FIELDS.has(row.items[0].field) ? group.count : 0);
  return (
    <section className="min-w-0 overflow-x-auto rounded-[4px] border border-slate-200 bg-white px-2 py-1.5">
      <div className="flex flex-wrap items-center gap-2 px-1 pb-1">
        <span className="text-[12.5px] font-bold text-slate-800">{group.title}</span>
        <span className="text-[11px] text-slate-500">{NOTES[group.key]}</span>
        <span className="ml-auto flex items-center gap-2">
          {rows.length > 0 && group.error_today != null && group.error_suggested != null && (
            <HoverRows title="Écart entre le prévu et le réel" rows={[["Aujourd'hui", `${fmtDuration(group.error_today)} par visite`], ["Avec ces réglages", `${fmtDuration(group.error_suggested)} par visite`],
              ["Mesure", "médiane, en plus ou en moins"], ["Visites", `${group.count} ${MEASURES[group.key]}`]]}>
              <span className="cursor-help text-[11.5px] text-slate-600">Écart au réel <b className="tabular-nums">{`${fmtDuration(group.error_today)} → ${fmtDuration(group.error_suggested)}`}</b></span>
            </HoverRows>
          )}
          {rows.length > 0 && <DayEnd before={dayEnd} after={group.day_end} />}
          {!rows.length ? <span className="inline-block w-16"><GeotabBadge tone="ok">À jour</GeotabBadge></span> : onApply && (
            <button type="button" disabled={!selected.length} onClick={() => onApply({ ...group, changes: selected })}
              className="h-6 rounded-[4px] border border-accent bg-accent px-3 text-[12px] font-semibold text-white hover:border-accent-strong hover:bg-accent-strong focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40 disabled:border-slate-200 disabled:bg-slate-50 disabled:text-slate-400">
              {`Appliquer (${selected.length})`}
            </button>
          )}
        </span>
      </div>
      {group.notes?.map((note) => <p key={note} className="mx-1 mb-1 text-[11.5px] font-semibold text-accent">{note}</p>)}
      {group.validation && <Validation check={group.validation} kept={rows.length > 0} />}
      {partialBase && (chosenBase ? (
        <p role="status" className="mx-1 mb-1 text-[11.5px] font-semibold text-slate-600">Votre valeur sera retenue : après Appliquer, le Calibrage calcule pour elle les valeurs qui vont avec.</p>
      ) : (
        <p role="alert" className="mx-1 mb-1 text-[11.5px] font-semibold text-[#a86a24]">{group.key === "route"
          ? "Temps fixe par trajet et vitesse vont ensemble : n'en appliquer qu'un fausse les temps de route."
          : group.key === "aspiration" ? "Temps aspirateur et débit d'aspiration vont ensemble : n'en appliquer qu'un fausse les temps prévus."
          : "Temps fixe et débit vont ensemble : n'en appliquer qu'un fausse les temps prévus."}</p>
      ))}
      {rows.length > 0 && (
        <table className="w-full min-w-0">
          <thead><tr>{["Réglage", "Où", "Actuel", "Valeur essayée", "Mesures", "Plage probable"].map((label) => <th key={label} className={HEAD}>{label}</th>)}</tr></thead>
          <tbody>
            {rows.map((row) => (
              <ChangeRow key={row.key} row={row} measures={measuresOf(row)} checked={!unchecked.has(row.key)} typed={typed[row.key]}
                onToggle={() => onToggle(row.key)} onType={(value) => onType(row.key, value)} />
            ))}
          </tbody>
        </table>
      )}
      {rowsOf(group.held || []).map((row) => <HeldRow key={row.key} row={row} measures={measuresOf(row)} noun={MEASURES[group.key]} />)}
      {/* Valeurs choisies par le répartiteur : plus proposées, la mesure reste en vue. */}
      {chosenRows.map(({ key, label, items: [first, ...rest] }) => (
        <p key={key} className="mx-1 mt-1 flex flex-wrap items-center gap-x-2 text-[11.5px] text-slate-600">
          <span><b className="font-semibold text-slate-800">{label}</b>{` : votre choix ${fmtValue(first.current, first.unit)} · mesuré ${fmtValue(first.suggested, first.unit)}`}</span>
          {onForget && <button type="button" onClick={() => onForget([first, ...rest].map((c) => `${c.kind}|${c.id}|${c.field}`))} className={LINK}>Ne plus retenir</button>}
        </p>
      ))}
    </section>
  );
}

// Fenêtre de confirmation : chaque changement, avant → après, regroupé par fiche.
// onApplied rend la promesse du recalcul : le toast suit l'enregistrement puis l'actualisation, comme Sauvegarder ailleurs.
export function ApplyDialog({ group, onClose, onApplied }) {
  const { showToast, updateToast } = useToast();
  const [state, setState] = React.useState({ busy: false, error: "" });
  const confirm = async () => {
    setState({ busy: true, error: "" });
    const count = group.changes.length;
    const scope = `Calibrage · ${group.title} · ${count} réglage${count > 1 ? "s" : ""}`;
    const progress = createToastProgress([
      { key: "save", label: "Enregistrement des réglages" },
      { key: "dispatch", label: "Actualisation du Dispatch de la journée active" },
      { key: "refresh", label: "Recalcul du Calibrage" },
    ]);
    const toastId = showToast({ type: "loading", title: "Enregistrement en cours…", message: scope, duration: null, progress });
    const report = createToastProgressReporter(progress, (next) => updateToast(toastId, { progress: next }));
    const finish = (type, title, details, duration) => updateToast(toastId, { type, title, message: [scope, details].filter(Boolean).join(" · "), duration });
    try {
      await applyGeotabCalibration(group.changes);
    } catch (error) {
      report.finish({ status: "failed" });
      finish("error", "Réglages non enregistrés", error.message, 8000);
      setState({ busy: false, error: error.message });
      return;
    }
    report("save", "completed");
    // Comme « Actualiser » sur chaque carte de Mise à jour ; ce qui ne passe pas y reste.
    report("dispatch");
    let dispatchMessage = null;
    let toReview = 0;
    try {
      const dispatchDate = await resolveActiveDispatchDate();
      if (dispatchDate) {
        const summary = await refreshDispatchDay(dispatchDate, {
          meuneries: group.changes.some((c) => c.kind === "site"),
          onProgress: (step) => updateToast(toastId, { message: `${scope} · ${step}` }),
        });
        dispatchMessage = describeDispatchRefresh(dispatchDate, summary);
        toReview = summary.toReview;
      }
      report("dispatch", "completed");
    } catch (failure) {
      report("dispatch", "failed");
      dispatchMessage = `Dispatch non actualisé : ${failure?.message || "erreur"} Les changements restent dans Mise à jour.`;
      toReview = 1;
    }
    report("refresh");
    try {
      await onApplied(group);
      report("refresh", "completed");
      report.finish();
      finish(toReview ? "warning" : "success", "Réglages enregistrés", dispatchMessage, toReview ? 8000 : 5000);
    } catch (failure) {
      report("refresh", "failed");
      report.finish({ status: "failed" });
      finish("warning", "Enregistré — actualisation impossible", failure?.message || "Cliquez sur Calculer.", 7000);
    }
  };
  return (
    <ConfirmationModal title={`Appliquer les réglages « ${group.title} » ?`} tone="accent" panelSize="default"
      description={`Écrits comme une modification du Data Manager. Les Dispatchs ouverts afficheront ces changements dans Mise à jour.${
        group.changes.some((c) => c.chosen) ? " « Votre choix » : retenu, le Calibrage ne le reproposera plus et calculera pour lui les valeurs qui vont avec." : ""}`}
      confirmLabel={`Appliquer (${group.changes.length})`} isBusy={state.busy} errorMessage={state.error}
      onCancel={() => { if (!state.busy) onClose(); }} onConfirm={confirm}>
      <div className="max-h-[50vh] overflow-y-auto rounded-[4px] border border-slate-200">
        <table className="w-full min-w-0">
          <thead><tr>{["Fiche", "Réglage", "Avant", "Après"].map((label) => <th key={label} className={`${HEAD} sticky top-0 bg-white`}>{label}</th>)}</tr></thead>
          <tbody>
            {group.changes.map((c) => (
              <tr key={`${c.kind}-${c.id}-${c.field}`}>
                <td className={`${CELL} font-semibold text-slate-800`}>{c.kind === "setting" ? SOURCES.setting : c.label}</td>
                <td className={`${CELL} text-slate-600`}>{c.field_label}</td>
                <td className={`${CELL} text-slate-500`}>{fmtValue(c.current, c.unit)}</td>
                <td className={`${CELL} font-semibold text-slate-900`}>{fmtValue(c.suggested, c.unit)}
                  {c.chosen && <span className="ml-1.5 text-[11px] font-normal text-slate-500">votre choix</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </ConfirmationModal>
  );
}

// Onglet « À ajuster » : les réglages que les trajets réels suggèrent, à simuler puis à appliquer.
// En simulation, les suggestions restantes se calculent à partir des valeurs essayées.
export default function GeotabAdjustments({ groups, dayEnd, prudent, canApply, simulating, onSimulate, onApplied, onForget }) {
  const [confirming, setConfirming] = React.useState(null);
  const [unchecked, setUnchecked] = React.useState(() => new Set());
  const [typed, setTyped] = React.useState({});
  if (!groups?.length) return <p className="px-1 text-[12px] text-slate-500">Aucun réglage à suggérer sur la période.</p>;
  const toggle = (key) => setUnchecked((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });
  const type = (key, value) => setTyped((prev) => ({ ...prev, [key]: value }));
  const selected = selectedChanges(groups, unchecked, typed);
  return (
    <div className="grid min-w-0 gap-2">
      <div className="flex flex-wrap items-center gap-2 text-[12px] text-slate-600">
        <button type="button" disabled={!selected.length} onClick={() => onSimulate(selected)}
          className="h-7 rounded-[4px] border border-slate-300 bg-white px-3 text-[12.5px] font-semibold text-slate-700 hover:border-slate-400 hover:bg-slate-50 hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40 disabled:border-slate-200 disabled:bg-slate-50 disabled:text-slate-400">
          {`Simuler (${selected.length})`}
        </button>
        <span>{simulating ? "Ajoute les lignes cochées à la simulation en cours." : "Refait tout le Calibrage avec les lignes cochées, sans rien écrire. Les valeurs se modifient avant de simuler."}</span>
      </div>
      {prudent && <PrudentTable prudent={prudent} />}
      <div className="grid grid-cols-2 items-start gap-2 max-[1300px]:grid-cols-1">
        {groups.map((group) => (
          <GroupCard key={group.key} group={group} dayEnd={dayEnd} unchecked={unchecked} typed={typed} onToggle={toggle} onType={type}
            onApply={canApply && !simulating ? setConfirming : null} onForget={canApply && !simulating ? onForget : null} />
        ))}
      </div>
      {confirming && (
        <ApplyDialog group={confirming} onClose={() => setConfirming(null)}
          onApplied={(g) => { setConfirming(null); return onApplied(g); }} />
      )}
    </div>
  );
}
